import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, CalendarClock, Settings2, TrendingUp, TrendingDown, HelpCircle, Target, Layers, Gauge, CheckCircle2, AlertTriangle, RefreshCw, X } from 'lucide-react';

const STATE_STYLE = {
  achieved: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30',
  partially_achieved: 'text-amber-300 bg-amber-500/15 border-amber-500/30',
  not_achieved: 'text-rose-300 bg-rose-500/15 border-rose-500/30',
  inconclusive: 'text-slate-300 bg-slate-500/15 border-slate-500/30',
  waiting_for_data: 'text-sky-300 bg-sky-500/15 border-sky-500/30',
  needs_review: 'text-amber-300 bg-amber-500/15 border-amber-500/30',
};

const TYPE_LABEL = {
  rate_change: 'Rate change', reinsurance_change: 'Reinsurance change',
  reserve_strengthening: 'Reserve strengthening', underwriting_change: 'Underwriting change',
  product_launch: 'Product launch', catastrophe: 'Catastrophe', regulatory: 'Regulatory', other: 'Other',
};
const DIR = {
  positive: { icon: TrendingUp, cls: 'text-emerald-300', label: 'Expected positive' },
  negative: { icon: TrendingDown, cls: 'text-rose-300', label: 'Expected negative' },
  uncertain: { icon: HelpCircle, cls: 'text-amber-300', label: 'Direction uncertain' },
};

export default function ResultsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [measuring, setMeasuring] = useState(false);
  const [ackTarget, setAckTarget] = useState(null);
  const [ackReason, setAckReason] = useState('');
  const [ackBusy, setAckBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([api.getBusinessTimeline(), api.listResults()]);
      setEvents(t.events || []);
      setResults(r.results || []);
    } catch (e) {
      toast.error(e.message || 'Could not load results');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const measureNow = async () => {
    setMeasuring(true);
    try {
      const r = await api.autoRunResults();
      toast.success(r.message || 'Results measured');
      await load();
    } catch (e) {
      toast.error(e.message || 'Could not measure results');
    } finally {
      setMeasuring(false);
    }
  };
  const acknowledge = async () => {
    const res = ackTarget;
    if (res.high_impact && !ackReason.trim()) { toast.error('A reason is required for a high-impact result'); return; }
    setAckBusy(true);
    try {
      await api.acknowledgeResult(res.result_id, ackReason.trim());
      toast.success('Acknowledged');
      setAckTarget(null); setAckReason('');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setAckBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="results-page">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h1 className="text-3xl font-bold text-foreground tracking-tight">Results</h1>
        <div className="flex items-center gap-2">
          <button onClick={measureNow} disabled={measuring} data-testid="measure-results-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${measuring ? 'animate-spin' : ''}`} /> Measure results
          </button>
          <button onClick={() => navigate('/business-events')} data-testid="manage-events-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-muted/50 text-foreground hover:bg-muted transition-colors">
            <Settings2 className="w-4 h-4" /> Manage events
          </button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Results are measured automatically. Routine successes are recorded here and in your digest;
        only exceptions (inconclusive, guardrail-affecting, or materially different) become a task in My Work.
        Events give context — a result is never assumed to be caused by any one event.
      </p>

      {/* Measured results */}
      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
        <Gauge className="w-4 h-4" /> Measured results
      </h2>
      {loading ? (
        <div className="flex justify-center py-10 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : results.length === 0 ? (
        <div className="rounded-2xl border border-border/50 bg-card/30 p-6 text-center mb-8" data-testid="results-empty">
          <p className="text-sm text-muted-foreground">No results measured yet. Complete an action, then use “Measure results”.</p>
        </div>
      ) : (
        <div className="grid gap-3 mb-8" data-testid="results-list">
          {results.map((r) => (
            <div key={r.result_id} className="rounded-2xl border border-border/50 bg-card/40 p-4" data-testid={`result-${r.result_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border ${STATE_STYLE[r.state] || STATE_STYLE.inconclusive}`}>{r.state_label}</span>
                    {r.needs_review ? <span className="inline-flex items-center gap-1 text-[11px] text-amber-300"><AlertTriangle className="w-3.5 h-3.5" /> Needs review</span>
                      : r.acknowledged ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300"><CheckCircle2 className="w-3.5 h-3.5" /> Acknowledged</span>
                      : <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300"><CheckCircle2 className="w-3.5 h-3.5" /> Routine — in digest</span>}
                  </div>
                  <p className="text-sm font-semibold text-foreground mt-1.5">{r.title}</p>
                  <p className="text-[13px] text-muted-foreground mt-0.5">{r.interpretation}</p>
                  {(r.baseline || r.target) && <p className="text-[12px] text-muted-foreground/80 mt-1">{r.baseline || '—'} → {r.observed || '(measuring)'} · target {r.target || '—'}{r.measurement_period ? ` by ${r.measurement_period}` : ''}</p>}
                  {(r.exception_reasons || []).length > 0 && (
                    <ul className="mt-1 text-[12px] text-amber-200/80 list-disc pl-4">{r.exception_reasons.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  )}
                </div>
                {r.needs_review && (
                  <button onClick={() => { setAckTarget(r); setAckReason(''); }} data-testid={`ack-result-${r.result_id}`} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 whitespace-nowrap">Acknowledge</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
        <CalendarClock className="w-4 h-4" /> Business Timeline
      </h2>

      {loading ? (
        <div className="flex justify-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-border/50 bg-card/30 p-10 text-center" data-testid="timeline-empty">
          <p className="text-base font-medium text-foreground">No business events recorded yet.</p>
          <p className="text-sm text-muted-foreground mt-1">Log rate changes, catastrophes, regulatory changes and the like so results can be interpreted in context.</p>
          <button onClick={() => navigate('/business-events')} className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90">Add an event</button>
        </div>
      ) : (
        <div className="relative pl-6" data-testid="business-timeline">
          <div className="absolute left-2 top-1 bottom-1 w-px bg-border/50" />
          {events.map((e) => {
            const d = DIR[e.expected_direction] || DIR.uncertain;
            const DIcon = d.icon;
            return (
              <div key={e.event_id} className="relative mb-5" data-testid={`timeline-event-${e.event_id}`}>
                <div className="absolute -left-[18px] top-1 w-3 h-3 rounded-full bg-primary/70 ring-4 ring-background" />
                <div className="rounded-xl border border-border/40 bg-card/40 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold text-foreground">{e.name}</span>
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">{e.event_date}{e.end_date ? ` → ${e.end_date}` : ''}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[11px]">
                    <span className="px-2 py-0.5 rounded-md bg-muted/50 text-muted-foreground">{TYPE_LABEL[e.event_type] || e.event_type}</span>
                    <span className={`inline-flex items-center gap-1 ${d.cls}`}><DIcon className="w-3.5 h-3.5" /> {d.label}</span>
                    {(e.applies_to || []).map((a) => <span key={a} className="px-2 py-0.5 rounded-md bg-muted/40 text-muted-foreground capitalize">{a}</span>)}
                  </div>
                  {e.description && <p className="text-[13px] text-muted-foreground mt-2">{e.description}</p>}
                  {(e.affected_metrics || []).length > 0 && (
                    <p className="text-[12px] text-muted-foreground/80 mt-1">Affects: {e.affected_metrics.join(', ')}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {e.related_goal_name && <span className="inline-flex items-center gap-1 text-[11px] text-primary"><Target className="w-3 h-3" /> {e.related_goal_name}</span>}
                    {e.related_initiative_name && <span className="inline-flex items-center gap-1 text-[11px] text-violet-300"><Layers className="w-3 h-3" /> {e.related_initiative_name}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {ackTarget && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" data-testid="ack-modal">
          <div className="w-full max-w-md rounded-2xl border border-border/60 bg-background shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-border/40">
              <h4 className="text-base font-semibold text-foreground">Acknowledge result</h4>
              <button onClick={() => setAckTarget(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-muted-foreground">{ackTarget.title} — <span className="font-medium text-foreground">{ackTarget.state_label}</span></p>
              {ackTarget.high_impact && <p className="text-[13px] text-amber-300 bg-amber-500/10 rounded-lg p-2.5">This is a high-impact result, so a reason is required and kept in the history.</p>}
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">{ackTarget.high_impact ? 'Reason (required)' : 'Note (optional)'}</label>
                <textarea data-testid="ack-reason" value={ackReason} onChange={(e) => setAckReason(e.target.value)} rows={3} className="w-full mt-1 px-3 py-2 rounded-lg bg-muted/40 border border-border/40 text-sm text-foreground outline-none focus:border-primary/40 resize-none" placeholder="Explain your review…" />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-border/40">
              <button onClick={() => setAckTarget(null)} className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted/50">Cancel</button>
              <button data-testid="ack-confirm" disabled={ackBusy || (ackTarget.high_impact && !ackReason.trim())} onClick={acknowledge} className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">{ackBusy ? 'Working…' : 'Confirm acknowledgment'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
